// The countries the payer phone control offers, as data.
//
// Ruled 2026-09-03: the control is a country dropdown plus a national number,
// defaulted to Cameroon, validating the national-number length PER SELECTED
// COUNTRY. The point of choosing that over a fixed "+237" prefix was that a
// second country becomes a row here rather than a code change -- so the
// registry exists even while it holds one row, and adding a country is an edit
// to this file and nothing else.
//
// ⛔ Never write a bare `phoneNumber.length === 9` against this. That is the
// registry violation configured-fields-ownership.md rule 7 names, it is wrong
// for the first non-CM country added, and it is wrong in the worse direction:
// it accepts a number that cannot be debited.

export type PhoneCountry = {
  // ISO 3166-1 alpha-2, and the value the control stores.
  code: string;
  // The international dialling code, shown beside the field. It is NOT sent:
  // see the wire note on payerPhoneNumber below.
  dialCode: string;
  // Exact length of the national number, in digits.
  nationalLength: number;
  // Shown as the field's placeholder, so the expected shape is visible before
  // a payer gets it wrong.
  example: string;
  // Digit groups shown while typing and on every screen, e.g. 677 21 48 90.
  // Display only: the number is stored and sent without them.
  groups: readonly number[];
};

// ⚠ ONE ROW, DELIBERATELY. Cameroon's nine-digit national number is the only
// length this workspace can prove: it is what the live deposit rail sends
// today (phoneNumberInput.tsx slices to 9) and what the payment platform
// debits. The mockup sketches TD, CF and GA beside it, and their numbering
// plans are NOT verified here -- shipping an unverified length would accept a
// number that cannot be debited, which is the exact cost the ruling accepted
// for making the country selectable and the exact one worth not paying yet.
// Adding them is a row each, once the plans are confirmed (TODO-268).
export const PHONE_COUNTRIES: readonly PhoneCountry[] = Object.freeze([
  { code: "CM", dialCode: "+237", nationalLength: 9, example: "670112233", groups: [3, 2, 2, 2] },
]);

export const DEFAULT_PHONE_COUNTRY = "CM";

export const phoneCountry = (code: string): PhoneCountry | undefined =>
  PHONE_COUNTRIES.find(country => country.code === code);

// The value the pay body carries as `instrument_account_number`.
//
// ⚠ It is the NATIONAL number, with no dialling code, because that is what the
// rail sends today: the Go pay leg passes the trimmed value straight through
// to the payment platform without normalising it, and the deposit journey has
// always sent nine bare digits. Prefixing a country code here would change the
// string that reaches the platform on a live money path, to no reader's
// benefit -- nothing downstream parses one. The mockup annotates the wire the
// other way; that annotation is a proposal about a field no ruling settled,
// and the live rail wins over it. Filed as TODO-269.
export const payerPhoneNumber = (national: string): string => national.trim();

// The forms a configured phone pattern may be written against. CORE describes
// the parent number as the service sees it -- the live school-fee services send
// `/^\+237[236]\d{8}$/` -- while the portal holds and sends the national digits.
// Testing both keeps a school's rule meaningful without changing what is held
// or what the payment carries.
export const phonePatternCandidates = (
  national: string,
  countryCode: string
): string[] => {
  const country = phoneCountry(countryCode);
  const digits = national.trim();

  if (!country || digits === "") return [digits];

  // With and without the plus: a pattern written `^237...` is as plausible as
  // one written `^\+237...`, and neither form may reject a valid number.
  return [
    digits,
    `${country.dialCode}${digits}`,
    `${country.dialCode.slice(1)}${digits}`,
  ];
};

export const isCompleteNationalNumber = (
  national: string,
  countryCode: string
): boolean => {
  const country = phoneCountry(countryCode);

  // An unknown country cannot be validated, so it cannot be paid from. This
  // is only reachable if state is set to a code the registry never offered.
  if (!country) return false;

  return new RegExp(`^\\d{${country.nationalLength}}$`).test(national.trim());
};
